import { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Lock, Eye, EyeOff, ShieldCheck, ShieldOff, LogOut, Copy, Check } from 'lucide-react';
import { useUser } from '@hooks/useUser';
import { useAuth } from '@hooks/useAuth';
import { authService } from '@services/authService';
import Card from '@components/common/Card';
import Input from '@components/common/Input';
import Button from '@components/common/Button';
import Modal from '@components/common/Modal';
import toast from 'react-hot-toast';

const passwordSchema = z.object({
  currentPassword: z.string().min(1, 'Informe a senha atual'),
  newPassword: z.string().min(8, 'Nova senha deve ter no minimo 8 caracteres'),
  confirmPassword: z.string().min(1, 'Confirme a nova senha'),
}).refine(data => data.newPassword === data.confirmPassword, {
  message: 'As senhas nao coincidem',
  path: ['confirmPassword'],
});

const Security = () => {
  const { changePassword, isChangingPassword } = useUser();
  const { logout, user } = useAuth();
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showSessionModal, setShowSessionModal] = useState(false);

  // 2FA state
  const [twoFAEnabled, setTwoFAEnabled] = useState(user?.twoFactorEnabled || false);
  const [setupStep, setSetupStep] = useState(null); // null | 'qrcode' | 'verify' | 'backup' | 'disable'
  const [qrCode, setQrCode] = useState(null);
  const [secret, setSecret] = useState(null);
  const [backupCodes, setBackupCodes] = useState([]);
  const [verifyCode, setVerifyCode] = useState(['', '', '', '', '', '']);
  const [disablePassword, setDisablePassword] = useState('');
  const [is2FALoading, setIs2FALoading] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [copiedBackup, setCopiedBackup] = useState(false);
  const codeRefs = useRef([]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ resolver: zodResolver(passwordSchema) });

  const handleChangePassword = async (data) => {
    await changePassword({
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
    });
    reset();
  };

  const PasswordInput = ({ label, name, show, onToggle, error }) => (
    <Input
      label={label}
      type={show ? 'text' : 'password'}
      leftIcon={<Lock size={16} />}
      rightIcon={
        <button
          type="button"
          onClick={onToggle}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      }
      error={error}
      {...register(name)}
    />
  );

  // 2FA handlers
  const handleSetup2FA = async () => {
    try {
      setIs2FALoading(true);
      const response = await authService.setup2FA();
      setQrCode(response.qrCode);
      setSecret(response.secret);
      setBackupCodes(response.backupCodes);
      setSetupStep('qrcode');
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Erro ao iniciar configuracao do 2FA');
    } finally {
      setIs2FALoading(false);
    }
  };

  const handleCodeChange = (index, value) => {
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newCode = [...verifyCode];
      digits.forEach((d, i) => {
        if (index + i < 6) newCode[index + i] = d;
      });
      setVerifyCode(newCode);
      const nextIndex = Math.min(index + digits.length, 5);
      codeRefs.current[nextIndex]?.focus();
      return;
    }
    const newCode = [...verifyCode];
    newCode[index] = value.replace(/\D/g, '');
    setVerifyCode(newCode);
    if (value && index < 5) {
      codeRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !verifyCode[index] && index > 0) {
      codeRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify2FA = async () => {
    const code = verifyCode.join('');
    if (code.length < 6) return;
    try {
      setIs2FALoading(true);
      await authService.verify2FA(code);
      toast.success('2FA ativado com sucesso!');
      setSetupStep('backup');
      setTwoFAEnabled(true);
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Codigo invalido');
    } finally {
      setIs2FALoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!disablePassword) {
      toast.error('Informe sua senha');
      return;
    }
    try {
      setIs2FALoading(true);
      await authService.disable2FA(disablePassword);
      toast.success('2FA desativado');
      setTwoFAEnabled(false);
      setSetupStep(null);
      setDisablePassword('');
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Erro ao desativar 2FA');
    } finally {
      setIs2FALoading(false);
    }
  };

  const handleCopySecret = () => {
    navigator.clipboard.writeText(secret);
    setCopiedSecret(true);
    setTimeout(() => setCopiedSecret(false), 2000);
  };

  const handleCopyBackup = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    setCopiedBackup(true);
    toast.success('Codigos copiados!');
    setTimeout(() => setCopiedBackup(false), 2000);
  };

  const closeSetup = () => {
    setSetupStep(null);
    setQrCode(null);
    setSecret(null);
    setVerifyCode(['', '', '', '', '', '']);
    setDisablePassword('');
  };

  const sessionInfo = {
    device: navigator.userAgent.includes('Mobile') ? 'Dispositivo Movel' : 'Computador',
    browser: (() => {
      const ua = navigator.userAgent;
      if (ua.includes('Chrome')) return 'Chrome';
      if (ua.includes('Firefox')) return 'Firefox';
      if (ua.includes('Safari')) return 'Safari';
      return 'Navegador';
    })(),
    location: 'Brasil',
    current: true,
  };

  return (
    <div className="space-y-6">
      {/* Alterar senha */}
      <Card title="Alterar Senha" subtitle="Use uma senha forte com letras, numeros e simbolos">
        <form onSubmit={handleSubmit(handleChangePassword)} className="space-y-4 max-w-md">
          <PasswordInput
            label="Senha atual"
            name="currentPassword"
            show={showCurrent}
            onToggle={() => setShowCurrent(v => !v)}
            error={errors.currentPassword?.message}
          />
          <PasswordInput
            label="Nova senha"
            name="newPassword"
            show={showNew}
            onToggle={() => setShowNew(v => !v)}
            error={errors.newPassword?.message}
          />
          <PasswordInput
            label="Confirmar nova senha"
            name="confirmPassword"
            show={showConfirm}
            onToggle={() => setShowConfirm(v => !v)}
            error={errors.confirmPassword?.message}
          />

          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-xs text-blue-700 dark:text-blue-400 space-y-1">
            <p className="font-medium">Requisitos:</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>Minimo de 8 caracteres</li>
              <li>Recomendado: letras maiusculas, minusculas, numeros e simbolos</li>
            </ul>
          </div>

          <div className="flex justify-end">
            <Button type="submit" isLoading={isChangingPassword}>
              Alterar Senha
            </Button>
          </div>
        </form>
      </Card>

      {/* Sessoes ativas */}
      <Card title="Sessoes Ativas" subtitle="Dispositivos com acesso a sua conta">
        <div className="space-y-3">
          <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                <ShieldCheck size={20} className="text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {sessionInfo.browser} — {sessionInfo.device}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {sessionInfo.location} · Sessao atual
                </p>
              </div>
            </div>
            <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-1 rounded-full font-medium">
              Ativo agora
            </span>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
          <Button
            variant="danger"
            leftIcon={<LogOut size={16} />}
            onClick={() => setShowSessionModal(true)}
          >
            Encerrar Todas as Sessoes
          </Button>
        </div>
      </Card>

      {/* Autenticacao de dois fatores */}
      <Card title="Autenticacao de Dois Fatores">
        {twoFAEnabled ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded-xl">
              <ShieldCheck size={24} className="text-green-600" />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">2FA ativado</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Sua conta tem uma camada extra de protecao
                </p>
              </div>
            </div>
            <Button
              variant="danger"
              size="sm"
              leftIcon={<ShieldOff size={16} />}
              onClick={() => setSetupStep('disable')}
            >
              Desativar 2FA
            </Button>
          </div>
        ) : (
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                Adicione uma camada extra de seguranca a sua conta com autenticacao de dois fatores (TOTP).
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Use aplicativos como Google Authenticator, Authy ou 1Password.
              </p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<ShieldCheck size={16} />}
              onClick={handleSetup2FA}
              isLoading={is2FALoading}
            >
              Configurar 2FA
            </Button>
          </div>
        )}
      </Card>

      {/* Modal Setup 2FA - QR Code */}
      <Modal
        isOpen={setupStep === 'qrcode'}
        onClose={closeSetup}
        title="Configurar 2FA"
        size="md"
      >
        <div className="space-y-6">
          <div className="text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Escaneie o QR code abaixo com seu aplicativo autenticador
            </p>
            {qrCode && (
              <div className="inline-block p-4 bg-white rounded-xl border border-gray-200">
                <img src={qrCode} alt="QR Code 2FA" className="w-48 h-48" />
              </div>
            )}
          </div>

          <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
              Ou insira manualmente a chave:
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-sm font-mono text-gray-900 dark:text-white break-all">
                {secret}
              </code>
              <button
                onClick={handleCopySecret}
                className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                {copiedSecret ? <Check size={16} className="text-green-500" /> : <Copy size={16} className="text-gray-400" />}
              </button>
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={() => setSetupStep('verify')}>
              Proximo
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Setup 2FA - Verificar codigo */}
      <Modal
        isOpen={setupStep === 'verify'}
        onClose={closeSetup}
        title="Verificar Codigo"
        size="sm"
      >
        <div className="space-y-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
            Digite o codigo de 6 digitos do seu aplicativo autenticador para confirmar
          </p>

          <div className="flex justify-center gap-2">
            {verifyCode.map((digit, i) => (
              <input
                key={i}
                ref={el => codeRefs.current[i] = el}
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={digit}
                onChange={e => handleCodeChange(i, e.target.value)}
                onKeyDown={e => handleCodeKeyDown(i, e)}
                className="w-11 h-13 text-center text-xl font-bold border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-colors"
                autoFocus={i === 0}
              />
            ))}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setSetupStep('qrcode')}>
              Voltar
            </Button>
            <Button
              onClick={handleVerify2FA}
              isLoading={is2FALoading}
              disabled={verifyCode.join('').length < 6}
            >
              Ativar 2FA
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Backup Codes */}
      <Modal
        isOpen={setupStep === 'backup'}
        onClose={closeSetup}
        title="Codigos de Backup"
        size="md"
      >
        <div className="space-y-6">
          <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl">
            <p className="text-sm text-yellow-700 dark:text-yellow-400 font-medium mb-1">
              Guarde esses codigos em um local seguro!
            </p>
            <p className="text-xs text-yellow-600 dark:text-yellow-500">
              Cada codigo so pode ser usado uma vez. Se perder acesso ao autenticador, use esses codigos para entrar na sua conta.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
            {backupCodes.map((code, i) => (
              <code key={i} className="text-sm font-mono text-gray-900 dark:text-white text-center py-1.5 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                {code}
              </code>
            ))}
          </div>

          <div className="flex justify-between">
            <Button
              variant="secondary"
              leftIcon={copiedBackup ? <Check size={16} /> : <Copy size={16} />}
              onClick={handleCopyBackup}
            >
              {copiedBackup ? 'Copiado!' : 'Copiar Codigos'}
            </Button>
            <Button onClick={closeSetup}>
              Concluir
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Desativar 2FA */}
      <Modal
        isOpen={setupStep === 'disable'}
        onClose={closeSetup}
        title="Desativar 2FA"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Para desativar a autenticacao de dois fatores, confirme sua senha.
          </p>

          <Input
            label="Senha"
            type="password"
            leftIcon={<Lock size={16} />}
            value={disablePassword}
            onChange={e => setDisablePassword(e.target.value)}
          />

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={closeSetup}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={handleDisable2FA}
              isLoading={is2FALoading}
            >
              Desativar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de confirmacao de encerrar sessoes */}
      <Modal
        isOpen={showSessionModal}
        onClose={() => setShowSessionModal(false)}
        title="Encerrar Todas as Sessoes"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowSessionModal(false)}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={() => { logout(); }}>
              Encerrar e Sair
            </Button>
          </>
        }
      >
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Voce sera desconectado de todos os dispositivos, incluindo este.
          Precisara fazer login novamente para continuar.
        </p>
      </Modal>
    </div>
  );
};

export default Security;
